import {
  action,
  computed,
  makeAutoObservable,
  reaction,
  runInAction,
} from "mobx";
import UserStore from "../store/UserStore";
import UserVenuesStore from "../store/UserVenuesStore";
import SelectedVenueStore from "../store/SelectedVenueStore";
import ApiClient from "../ApiClient";

export type UserType = {
  userId: number;
  venueId: string;

  firstName: string;
  lastName: string;
  emailAddress: string;

  isAdmin: boolean;
};

export type CommandCentreVenue = {
  venueId: number;
  displayName: string;
  nameKeyStr: string;

  commCenAppIconUrl: string;
};

export default class UserState {
  isLoggedIn: boolean = false;

  user?: UserType = undefined;

  selectedVenue?: CommandCentreVenue = undefined;

  userVenues?: CommandCentreVenue[] = undefined;

  loginInProgress = false;

  venueSearchTerm = "";

  constructor() {
    makeAutoObservable(this);

    let user = UserStore.getUser();
    let userVenues = UserVenuesStore.getVenues();

    if (user !== null && userVenues !== null) {
      this.isLoggedIn = true;
      this.user = user;
      this.userVenues = userVenues;

      // Restore the previously selected venue (persisted across reloads) so that
      // direct deep links (e.g. /reporting) work after a refresh for multi-venue
      // users. Validate against the current venue list so a stale/removed venue
      // is ignored, then fall back to the sole venue when there is only one.
      const persistedVenue = SelectedVenueStore.getVenue();
      const restoredVenue = persistedVenue
        ? userVenues?.find((venue) => venue.venueId === persistedVenue.venueId)
        : undefined;

      if (restoredVenue) {
        this.selectedVenue = restoredVenue;
      } else if (this.userVenues?.length === 1) {
        this.selectedVenue = this.userVenues[0];
      }
    }

    // Keep the persisted selected venue in sync with in-memory state. This
    // catches every assignment site (venue switcher, select-venue page, login
    // auto-select, logout reset) without each having to persist explicitly.
    reaction(
      () => this.selectedVenue,
      (venue) => {
        if (venue) {
          SelectedVenueStore.storeVenue(venue);
        } else {
          SelectedVenueStore.clearStore();
        }
      }
    );
  }

  @computed get getUserId() {
    return this.user?.userId;
  }

  @computed get userVenuesAlphabetical() {
    let venues = this.userVenues?.filter(
      (venue) => venue !== this.selectedVenue
    );

    if (this.venueSearchTerm !== undefined) {
      venues = venues?.filter((venue) =>
        venue.displayName.toLowerCase().includes(this.venueSearchTerm)
      );
    }

    return venues?.sort((venueOne, venueTwo) => {
      if (venueOne.displayName > venueTwo.displayName) {
        return 1;
      }

      if (venueOne.displayName < venueTwo.displayName) {
        return -1;
      }

      return 0;
    });
  }

  @computed get getVenueId() {
    return this.selectedVenue?.venueId;
  }

  @computed get isAdmin() {
    return this.user !== undefined && this.user.isAdmin;
  }

  @action
  login(user: UserType, userVenues: CommandCentreVenue[]) {
    UserStore.storeUser(user);
    UserVenuesStore.storeVenues(userVenues);

    this.user = user;
    this.userVenues = userVenues;

    if (this.userVenues?.length === 1) {
      this.selectedVenue = this.userVenues[0];
    }

    this.isLoggedIn = true;

    this.loginInProgress = false;
  }

  refreshUserVenues() {
    if (!this.isLoggedIn) return;

    ApiClient.refreshUserVenues()
      .then((response: any) => {
        this.login(response.data.user, response.data.commandCentreVenues);
      })
      .catch((error) => {});
  }

  logout() {
    document.cookie =
      "OK2P-AUTH" + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

    ApiClient.userLogout().then((response) => {
      runInAction(() => {
        this.isLoggedIn = false;
        this.user = undefined;
        this.selectedVenue = undefined;
        this.userVenues = undefined;
        this.loginInProgress = false;

        UserStore.clearStore();
        UserVenuesStore.clearStore();
        SelectedVenueStore.clearStore();
      });
    });
  }
}

export const userState = new UserState();
